import React, { FC, useState, useEffect } from "react";
import FadeIn from "react-fade-in";
import { FiActivity, FiSearch, FiX } from "react-icons/fi";
import Table from "../../../atom/table";
import PaginationButtons from "../../../atom/pagination";
import Lottiefy from "../../../atom/lottie/lottie";
import * as loaderLottie from "../../../utils/lottie/loaderLottie.json";
import { getTranscriptionAnalytics } from "../../../services/api";

interface TranscriptionProcessingProps {
  startDate: string;
  endDate: string;
  refreshTrigger?: number;
  data?: any;
}

const TranscriptionProcessing: FC<TranscriptionProcessingProps> = ({
  startDate,
  endDate,
  refreshTrigger,
  data,
}) => {
  const [pageNumber, setPageNumber] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState<any[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [hasFetched, setHasFetched] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [selectedStatus, setSelectedStatus] = useState<string>("ALL");

  const [sortColumn, setSortColumn] = useState<string>("created_at");
  const [sortDirection, setSortDirection] = useState<string>("desc");

  const [tableTitles, setTableTitles] = useState<any[]>([
    {
      title: "Created At",
      key: "created_at",
      class: "w-[20%]",
      sort: { direction: "desc", active: true },
    },
    {
      title: "Job Id",
      key: "id",
      class: "w-[40%]",
      sort: { direction: "asc", active: false },
    },
    {
      title: "Duration",
      key: "duration",
      class: "w-[15%]",
      sort: { direction: "asc", active: false },
    },
    {
      title: "STT Time",
      key: "stt_time",
      class: "w-[10%]",
      sort: { direction: "asc", active: false },
    },
    {
      title: "Status",
      key: "status",
      class: "w-[15%]",
      sort: { direction: "asc", active: false },
    },
  ]);

  const onHandleSort = (node: any, orderBy: string) => {
    let orderKey = node.key;
    if (orderKey === "id") {
      orderKey = "job_id";
    } else if (orderKey === "duration") {
      orderKey = "audio_duration";
    } else if (orderKey === "stt_time") {
      orderKey = "process_duration";
    }
    setSortColumn(orderKey);
    setSortDirection(orderBy.toLowerCase());
  };

  useEffect(() => {
    setPageNumber(1);
    setSearchInput("");
    setSearchQuery("");
    setSelectedStatus("ALL");
    setSortColumn("created_at");
    setSortDirection("desc");
    setTableTitles((prev) =>
      prev.map((col) => {
        if (col.key === "created_at") {
          return { ...col, sort: { direction: "desc", active: true } };
        } else if (col.sort) {
          return { ...col, sort: { direction: "asc", active: false } };
        }
        return col;
      })
    );
  }, [startDate]);

  useEffect(() => {
    const fetchPaginatedLogs = async () => {
      setLoading(true);
      try {
        const res = await getTranscriptionAnalytics(
          "logs",
          startDate,
          pageNumber,
          perPage,
          searchQuery ? searchQuery : undefined,
          sortDirection,
          sortColumn,
          selectedStatus
        );
        const resData = res?.data ?? res;
        setRecords(resData?.records ?? []);
        setTotalRecords(resData?.total ?? resData?.records?.length ?? 0);
        setHasFetched(true);
      } catch (err) {
        console.error("Error fetching paginated logs:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchPaginatedLogs();
  }, [startDate, pageNumber, perPage, refreshTrigger, sortColumn, sortDirection, searchQuery, selectedStatus]);

  const activeRecords = hasFetched ? records : (data?.records ?? []);
  const activeTotal = hasFetched ? totalRecords : (data?.total ?? 0);

  const formatLocalDate = (dateStr: string) => {
    if (!dateStr) return "";
    try {
      const parsedDate = dateStr.includes("T") && !dateStr.endsWith("Z") && !dateStr.includes("+")
        ? new Date(`${dateStr}Z`)
        : new Date(dateStr);
      return isNaN(parsedDate.getTime()) ? dateStr : parsedDate.toLocaleString();
    } catch (e) {
      return dateStr;
    }
  };

  const formattedData = (activeRecords as any[])
    .map((row: any) => {
    const rawDuration = row.audio_duration ?? row.duration;
    const formattedDuration = typeof rawDuration === "number"
      ? `${rawDuration.toFixed(1)}s`
      : rawDuration;

    const rawSttTime = row.process_duration ?? row.stt_time;
    const formattedSttTime = typeof rawSttTime === "number"
      ? `${rawSttTime.toFixed(1)}s`
      : rawSttTime;

    const statusText = row.status ? row.status.toUpperCase() : "COMPLETED";

    let statusClass = "bg-green-50 text-green-600 border-green-200";
    let statusIcon = "✓";

    if (statusText === "PROCESSING" || statusText === "PENDING" || statusText === "IN_PROGRESS" || statusText === "RUNNING") {
      statusClass = "bg-blue-50 text-blue-600 border-blue-200";
      statusIcon = "↻";
    } else if (statusText === "FAILED" || statusText === "ERROR" || statusText === "FAIL") {
      statusClass = "bg-red-50 text-red-600 border-red-200";
      statusIcon = "✗";
    }

    return {
      ...row,
      id: row.job_id ?? row.id,
      created_at: formatLocalDate(row.created_at),
      duration: <span className="text-gray-800">{formattedDuration}</span>,
      stt_time: formattedSttTime,
      status: (
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${statusClass}`}>
          <span className="font-bold">{statusIcon}</span>
          {statusText}
        </span>
      ),
    };
  });

  return (
    <div className="flex flex-col gap-8 w-full">
      {/* Total Records Card and Centered Search Bar */}
      <FadeIn>
        <div className="flex flex-col md:flex-row md:items-center gap-6 w-full">
          {/* Card */}
          <div className="bg-white p-6 rounded-3xl shadow-sm border border-gray-100 flex items-center gap-6 w-full md:max-w-[320px]">
            <div className="p-4 bg-blue-50 text-blue-500 rounded-2xl shadow-inner">
                <FiActivity size={28} />
            </div>
            <div>
                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">Total Records</p>
                <h3 className="text-4xl font-extrabold text-slate-800">{activeTotal}</h3>
            </div>
          </div>

          {/* Centered Search and Status Tabs Bar */}
          <div className="flex-1 flex flex-col sm:flex-row items-center justify-center gap-4 w-full">
            
            {/* Search Bar */}
            <div className="relative w-full max-w-xs">
              <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="text"
                placeholder="Search by Job ID..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    setSearchQuery(searchInput);
                    setPageNumber(1);
                  }
                }}
                className="w-full pl-11 pr-10 py-3 bg-[#ffffff] border border-gray-200 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 shadow-sm transition-all font-medium"
              />
              {searchInput && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchInput("");
                    setSearchQuery("");
                    setPageNumber(1);
                  }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <FiX size={18} />
                </button>
              )}
            </div>
            {/* Status Tabs */}
            <div className="flex bg-gray-100 p-1 rounded-2xl border border-gray-200">
              {["ALL", "COMPLETED", "FAILED"].map((status) => (
                <button
                  key={status}
                  type="button"
                  onClick={() => {
                    setSelectedStatus(status);
                    setPageNumber(1);
                  }}
                  className={`px-4 py-2 rounded-xl text-xs font-bold transition-all ${
                    selectedStatus === status
                      ? "bg-white text-blue-600 shadow-sm"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  {status}
                </button>
              ))}
            </div>
          </div>

          {/* Spacer to balance the layout for desktop centering */}
          <div className="hidden md:block md:w-[320px] md:invisible"></div>
        </div>
      </FadeIn>

      {/* Table Section */}
      <FadeIn delay={100}>
        <div className="bg-white rounded-[20px] shadow-sm border border-gray-100 overflow-hidden">
            {loading ? (
              <div className="flex h-64 items-center justify-center">
                <Lottiefy loop={true} json={loaderLottie} height={100} width={100} />
              </div>
            ) : (
              <Table 
                  tableTitles={tableTitles}
                  setTableTitles={setTableTitles}
                  tableData={formattedData}
                  onHandleSort={onHandleSort}
                  width="w-full"
                  scrollHeight="h-[calc(100vh-450px)]"
                  rowColorClass="bg-white"
              />
            )}
        </div>
      </FadeIn>

      {!loading && activeTotal > 0 && (
        <PaginationButtons
          pageNumber={pageNumber}
          handleChange={(pNUm: number) => setPageNumber(pNUm)}
          pageCount={activeTotal}
          perPage={perPage}
          handlePerPage={(value: any) => {
            setPerPage(parseInt(value));
            setPageNumber(1);
          }}
        />
      )}
    </div>
  );
};

export default TranscriptionProcessing;
